import { useCallback, useRef, useState } from "react";
import {
  pipelineService,
  type TranslateFrBatchParams,
  type TranslateFrFailure,
} from "../../../api/pipeline.service.js";

export type TranslateFrProgress = {
  processed: number;
  skipped: number;
  failed: number;
  remaining: number | null;
  batch: number;
};

const EMPTY_PROGRESS: TranslateFrProgress = {
  processed: 0,
  skipped: 0,
  failed: 0,
  remaining: null,
  batch: 0,
};

// Drives the UI batch loop: repeatedly calls translateFrBatch, accumulating
// totals, until remaining hits 0, a stop is requested, or an error/no-progress
// guard trips.
export function useTranslateFrBackfill(onComplete?: () => void) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<TranslateFrProgress>(EMPTY_PROGRESS);
  const [failures, setFailures] = useState<TranslateFrFailure[]>([]);
  const [error, setError] = useState<string | null>(null);
  const stopRef = useRef(false);

  const stop = useCallback(() => {
    stopRef.current = true;
  }, []);

  const start = useCallback(
    async (params: TranslateFrBatchParams) => {
      stopRef.current = false;
      setRunning(true);
      setError(null);
      setFailures([]);
      setProgress(EMPTY_PROGRESS);

      let processed = 0;
      let skipped = 0;
      let failed = 0;
      let batch = 0;
      const collected: TranslateFrFailure[] = [];

      try {
        while (!stopRef.current) {
          batch += 1;
          const result = await pipelineService.translateFrBatch(params);
          processed += result.processed;
          skipped += result.skipped;
          failed += result.failed;
          if (result.failures.length) {
            collected.push(...result.failures);
            setFailures([...collected]);
          }
          setProgress({ processed, skipped, failed, remaining: result.remaining, batch });

          if (result.remaining <= 0) break;

          // Runaway guard: work remains but nothing advanced this batch.
          if (result.processed === 0 && result.skipped === 0) {
            setError(
              `Stopped: batch ${batch} made no progress (${result.failed} failed) while ${result.remaining} remain.`,
            );
            break;
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Translation batch failed");
      } finally {
        setRunning(false);
        onComplete?.();
      }
    },
    [onComplete],
  );

  return { running, progress, failures, error, start, stop };
}
